import React, { useEffect, useState } from 'react';
import { Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField, Select, MenuItem, FormControl, InputLabel, Modal, IconButton } from '@mui/material';
import api from '../Api';
import ScanSummary from "./ScanSummary";
import NotScannedDialog from "./NotScannedDialog";
import { toast } from "react-toastify";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RefreshIcon from '@mui/icons-material/Refresh';
import { DEFAULT_PRINT_SLOTS, fetchAppConfig } from "../appConfig";
import { addDaysToDateString, getAppDateString } from "../utils/date";


const PrintsPage = () => {
  const [accounts, setAccounts] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [selectedDate, setSelectedDate] = useState(getAppDateString());
  const [selectedAccount, setSelectedAccount] = useState('');
  const [pickupOtc, setPickupOtc] = useState('');
  const [returnOtc, setReturnOtc] = useState('');
  const [otcModalOpen, setOtcModalOpen] = useState(false);
  // Flipkart return-OTC: START/END chooser + the live result shown big
  const [fkPick, setFkPick] = useState({ open: false, account: null });
  const [fkOtc, setFkOtc] = useState({ open: false, account: "", mode: "",
                                       loading: false, codes: "", note: "" });
  // Seconds the current fetch has been running. Ticks while loading and then
  // freezes, so the final number stays on screen — Flipkart comes back in ~1s
  // but Myntra goes through a slow tunnel, and without this the box just says
  // "fetching..." with no sense of whether it is progressing or wedged.
  const [otcElapsed, setOtcElapsed] = useState(0);
  // Clicking a red remaining count opens the not-scanned list, the same as on the
  // admin dashboard — this page rendered ScanSummary without the callback, so the
  // numbers were plain text here while being clickable there.
  const [nsOpen, setNsOpen] = useState(false);
  const [nsGroup, setNsGroup] = useState("");
  const [otcPresence, setOtcPresence] = useState({});
  const [allAccounts, setAllAccounts] = useState([]);
  const [scanSummary, setScanSummary] = useState([]);
  const [visibleAccounts, setVisibleAccounts] = useState([]);
  const [printSlots, setPrintSlots] = useState(DEFAULT_PRINT_SLOTS);
  const printSlotFields = printSlots.map(slot => slot.field_name);
  const printSlotLabels = printSlots.map(slot => slot.label);
  const onlyMoney = (v) => {
    const cleaned = String(v || "").replace(/[^\d.]/g, "");
    const [whole, ...decimalParts] = cleaned.split(".");
    return decimalParts.length ? `${whole}.${decimalParts.join("").slice(0, 2)}` : whole;
  };
  const [showPickupCols, setShowPickupCols] = useState(
    () => JSON.parse(localStorage.getItem("pickupCols") ?? "true")
  );
  const [showReturnCols, setShowReturnCols] = useState(
    () => JSON.parse(localStorage.getItem("returnCols") ?? "true")
  );

  const [qtyHidden, setQtyHidden] = useState(
    () => JSON.parse(localStorage.getItem("qtyHidden") ?? "true")
  );

  

  const getQty = (key) => {
    const val = parseInt(quantities[key]);
    return isNaN(val) ? 0 : val;
  };

  const loadAccountVisibility = (accounts) => {
    const activeAccountNames = accounts.map(a => a.name);

    // Read the user's saved visible selection (per-browser).
    let savedVisible = null;
    try {
      const raw = localStorage.getItem("visibleAccounts");
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed)) savedVisible = parsed;
    } catch { /* ignore malformed value */ }

    // First run ever → show all active accounts and seed the roster.
    if (!savedVisible) {
      setVisibleAccounts(activeAccountNames);
      localStorage.setItem("knownAccounts", JSON.stringify(activeAccountNames));
      return;
    }

    // Roster of accounts we've seen before. Any active account NOT in the
    // roster is newly added → default it to visible (instead of hiding it,
    // which is what caused e.g. MYE/MYS DIVINE to disappear after being added).
    let known = [];
    try {
      const rawKnown = localStorage.getItem("knownAccounts");
      const parsedKnown = rawKnown ? JSON.parse(rawKnown) : null;
      if (Array.isArray(parsedKnown)) known = parsedKnown;
    } catch { /* ignore */ }
    // Back-compat: no roster yet → treat the saved visible list as the roster,
    // so previously-added accounts still show on first load after this fix.
    if (known.length === 0) known = savedVisible;
    const knownSet = new Set(known);

    const visibleActiveAccounts = activeAccountNames.filter(
      name => savedVisible.includes(name) || !knownSet.has(name)
    );
    setVisibleAccounts(visibleActiveAccounts);

    // Keep the roster in sync with the current active set.
    localStorage.setItem("knownAccounts", JSON.stringify(activeAccountNames));
  };


  const fetchEntriesByDate = async (date) => {
    try {
      const response = await api.get(`/api/get-prints/${date}`);
      const fetchedAccounts = response.data.data;
  
      if (!fetchedAccounts || fetchedAccounts.length === 0) {
        setQuantities({});
        setAccounts([]);
        setOtcPresence({});
        console.warn("⚠️ No entries found for the selected date");
        return;
      }
  
      console.log(`✅ Entries fetched for date: ${date}`, fetchedAccounts);
  
      const newQuantities = {};
      const newOtcPresence = {};
  
      fetchedAccounts.forEach(account => {
        printSlotFields.forEach(field => {
          newQuantities[`${account.account_name}-${field}`] = account[field];
        });
        newQuantities[`${account.account_name}-pickup_qty`] = account.pickup_qty;
        newQuantities[`${account.account_name}-cancel_qty`] = account.cancel_qty;
        newQuantities[`${account.account_name}-cour_r_qty`] = account.cour_r_qty;
        newQuantities[`${account.account_name}-cust_r_qty`] = account.cust_r_qty;
        newQuantities[`${account.account_name}-remarks`] = account.remarks;
        newOtcPresence[`${account.account_name}-pickup_otc`] = !!account.pickup_otc;
        newOtcPresence[`${account.account_name}-return_otc`] = !!account.return_otc;
      });
  
      setQuantities(newQuantities);
      setAccounts(fetchedAccounts);
      setOtcPresence(newOtcPresence);
  
    } catch (error) {
      console.error("❌ Error fetching entries:", error.response?.data?.message || error.message);
      setQuantities({});
      setAccounts([]);
      setOtcPresence({});
    }
  };

  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setSelectedDate(newDate);
    fetchEntriesByDate(newDate);
  };

  const handleQuantityChange = (accountName, field, value) => {
    const key = `${accountName}-${field}`;
    setQuantities((prev) => ({ ...prev, [key]: value }));

    api.put('/api/update-qty', {
      accountName,
      [field]: value,
      date: selectedDate
    }).catch((err) => {
      console.error("❌ Failed to update qty:", err);
    });
  };

  const fetchAccounts = async () => {
    try {
      const res = await api.get("/api/accounts?active_only=true");
      setAllAccounts(res.data);
      toast.success("✅ Accounts table refreshed successfully!");
    } catch (err) {
      console.error("❌ Error fetching account list:", err);
      toast.error("❌ Failed to refresh accounts table.");
    }
  };
  
  const handleOtcAdd = async () => {
    if (!selectedAccount) {
      alert("❌ Please select an account first!");
      return;
    }
  
    try {
      console.log("📤 Sending OTC update request:", {
        accountName: selectedAccount,
        pickup_otc: pickupOtc,
        return_otc: returnOtc,
        date: selectedDate
      });
  
      await api.put('/api/update-qty', {
        accountName: selectedAccount,
        pickup_otc: pickupOtc,
        return_otc: returnOtc,
        date: selectedDate
      });
  
      console.log("✅ OTC updated successfully!");
      alert("✅ OTC updated!");
  
      // ✅ Close modal
      setOtcModalOpen(false);
  
      // ✅ Clear fields after submit
      setSelectedAccount(null);
      setPickupOtc(null);
      setReturnOtc(null);
  
      // ✅ Refetch data
      fetchEntriesByDate(selectedDate);
  
    } catch (err) {
      console.error("❌ Failed OTC update:", err);
      alert(err.response?.data?.message || "❌ Failed to update OTC!");
    }
  };
  
  
  const fetchScanSummary = async (date = selectedDate) => {
        try {
            const response = await api.get("/api/scan-summary", {
                params: { date }
            });
            setScanSummary(response.data);
        } catch (error) {
            console.error("❌ Error fetching scan summary:", error);
            toast.error("❌ Failed to fetch scan summary.");
        }
    };

  useEffect(() => {
    const loadConfig = async () => {
      const appConfig = await fetchAppConfig();
      setPrintSlots(appConfig.print_slots);
    };
    loadConfig();
    fetchAccounts();
    }, []);

    useEffect(() => {
    if (allAccounts.length) {
        loadAccountVisibility(allAccounts);
    }
    }, [allAccounts]);

    useEffect(() => {
    if (visibleAccounts.length) {
        localStorage.setItem("visibleAccounts", JSON.stringify(visibleAccounts));
    }
    }, [visibleAccounts]);

    useEffect(() => {
    localStorage.setItem("pickupCols", JSON.stringify(showPickupCols));
    localStorage.setItem("returnCols", JSON.stringify(showReturnCols));
    }, [showPickupCols, showReturnCols]);

  useEffect(() => {
          fetchEntriesByDate(selectedDate);
          fetchScanSummary(selectedDate); // ✅ Keeps summary in sync with selectedDate
      }, [selectedDate, printSlots]);

    useEffect(() => {
    if (!qtyHidden) return;

    const columns = document.querySelectorAll(`.qty-col, .qty-header, .qty-time-total`);
    columns.forEach(col => {
        col.style.display = "none";
    });
    }, [qtyHidden, allAccounts, quantities]);

  useEffect(() => {
    localStorage.setItem("qtyHidden", JSON.stringify(qtyHidden));
  }, [qtyHidden]);

  // The same conditions the manual flow always applied, now enforced for the
  // LIVE fetches too. Routing Flipkart and Myntra rows straight to the live
  // endpoints had bypassed them, so a code could be pulled before the counts
  // agreed. START is deliberately NOT covered: it is handed over on arrival,
  // before anything is counted.
  const otcGuard = (account, kind) => {
    const label = String(account.account_name || account.name);
    if (kind === "return") {
      const cour = getQty(`${label}-cour_r_qty`);
      const obd = getQty(`${label}-cust_r_qty`);
      return (cour > 0 || obd > 0)
        ? { ok: true }
        : { ok: false, why: "❌ FIRST FILL RETURN QTY (Cour R or OBD)" };
    }
    const summary = scanSummary.find((x) => x.account_name === label) || {};
    const scannedQuantity = Number(summary.scanned_quantity ?? summary.scanned_entries ?? 0);
    if (scannedQuantity <= 0) {
      return {
        ok: false,
        why: "❌ No scanned quantity found for this account/date",
      };
    }
    const pick = getQty(`${label}-pickup_qty`);
    const canc = getQty(`${label}-cancel_qty`);
    return pick + canc === scannedQuantity
      ? { ok: true }
      : { ok: false,
          why: `❌ Pickup quantities do not match scanned quantity!\nScanned: ${scannedQuantity}\nPickup ${pick} + Cancel ${canc} = ${pick + canc}` };
  };

  const handlePickupOtc = async (account) => {
    const g = otcGuard(account, "pickup");
    if (!g.ok) { alert(g.why); return; }
    // Flipkart: read the code Flipkart is showing right now, not the stored one.
    if (isFlipkartAccount(account) || isMyntraOtcAccount(account)) {
      if (isFlipkartAccount(account)) fetchFkPickupOtc(account);
      else fetchMyntraOtc(account, "pickup");
      return;
    }
    try {
      console.log("Account object received for pickup:", account);
  
      if (!account || !account.name) {
        console.error("🚨 Error: Account object is not defined or does not have a 'name' property!");
        alert("Error: Account object is missing or Account is undefined.");
        return;
      }
  
      const date = selectedDate;
      console.log(`🔍 Fetching total quantity for ${account.name} on ${date}`);
  
      // ✅ Get total quantity for the account on the selected date
      const summaryData = scanSummary.find(s => s.account_name === account.name);
      const totalAccountQty = Number(summaryData?.total_quantity ?? summaryData?.total_orders ?? 0);

  
      // If total qty is 0, still allow OTC check
        if (totalAccountQty === 0) {
        const response = await api.get(`/api/get-pickup-otc/${account.name}/${date}`);
        const { pickup_otc } = response.data;

        if (pickup_otc) {
            alert(`✅ Pickup OTC: ${pickup_otc}`);
        } else {
            alert("❌ No Pickup OTC found!");
        }
        return;
        }

  
      console.log(`✅ Total Quantity for ${account.name} on ${date}: ${totalAccountQty}`);
  
      const pickupQty = parseInt(quantities[`${account.name}-pickup_qty`] || 0);
      const cancelQty = parseInt(quantities[`${account.name}-cancel_qty`] || 0);
      const sumQty = pickupQty + cancelQty;
  
      console.log(`✅ Pickup: ${pickupQty}, Cancel: ${cancelQty}, Sum: ${sumQty}`);
  
      if (totalAccountQty === sumQty) {
        const response = await api.get(`/api/get-pickup-otc/${account.name}/${date}`);
        const { pickup_otc } = response.data;
  
        console.log(`✅ Fetched Pickup OTC: ${pickup_otc}`);
  
        if (pickup_otc) {
          alert(`✅ Pickup OTC: ${pickup_otc}\nPickup: ${pickupQty}\nCancel: ${cancelQty}`);
        } else {
          alert("❌ No valid Pickup OTC found!");
        }
      } else {
        alert(`❌ Quantities do not match!\nTotal Quantity: ${totalAccountQty}\nSum of Pickup & Cancel: ${sumQty}`);
      }
    } catch (error) {
      console.error("❌ Error fetching Pickup OTC:", error.response?.data?.message || error.message);
      alert("❌ Failed to fetch Pickup OTC!");
    }
  };
  

  // Flipkart return OTC can't be stored ahead of time the way Myntra's is — the
  // code only exists once the delivery agent (Wishmaster) is at the location, and
  // there are two of them (START on arrival, END after they finish scanning). So
  // for FK accounts the Return link asks which one and fetches it live.
  const isFlipkartAccount = (account) =>
    !!account && /^FK\s/i.test(String(account.account_name || account.name || ""));

  const fetchFkReturnOtc = async (account, mode) => {
    const label = String(account.account_name || account.name);
    setFkOtc({ open: true, account: label, mode, loading: true, codes: "", note: "" });
    try {
      const { data } = await api.post("/api/flipkart-otc/return",
        { account: label, mode });
      const id = data.id;
      const started = Date.now();
      const poll = setInterval(async () => {
        try {
          const { data: st } = await api.get(`/api/flipkart-otc/return/${id}`);
          const row = st.returnOtc && (st.returnOtc.results || [])[0];
          if (st.done || row) {
            clearInterval(poll);
            setFkOtc({
              open: true, account: label, mode, loading: false,
              codes: (row && row.codes) || "",
              note: (row && !row.codes && row.reason) ? row.reason
                    : (!row ? "no response from the runner" : ""),
            });
          } else if (Date.now() - started > 150000) {
            clearInterval(poll);
            setFkOtc({ open: true, account: label, mode, loading: false,
                       codes: "", note: "timed out — try again" });
          }
        } catch {
          /* keep polling through a blip */
        }
      }, 2000);
    } catch (err) {
      setFkOtc({ open: true, account: label, mode, loading: false, codes: "",
                 note: err?.response?.data?.error || "could not start the fetch" });
    }
  };

  // Flipkart pickup OTC. Two things happen on one click:
  //  1. the Pickup quantity is filled from Flipkart's own dispatch-ready count
  //     (same API pattern as the code itself, instead of being typed by hand);
  //  2. the code is only revealed once Pickup + Cancel matches the scanned total
  //     — the same guard the Myntra flow has always had, so the OTC can't be
  //     handed over while the counts still disagree.
  useEffect(() => {
    if (!fkOtc.open) { setOtcElapsed(0); return undefined; }
    if (!fkOtc.loading) return undefined;          // finished → freeze the number
    const started = Date.now();
    setOtcElapsed(0);
    const t = setInterval(() => setOtcElapsed((Date.now() - started) / 1000), 100);
    return () => clearInterval(t);
  }, [fkOtc.open, fkOtc.loading, fkOtc.account, fkOtc.mode]);

  // The runner reports short machine reasons; make them readable on the wall.
  const otcReason = (r) => ({
    no_otc: "No OTC issued by Flipkart yet",
    not_logged_in: "That account is logged out — log in on the Console",
    no_browser: "That account's browser is not running on the server",
  }[String(r || "")] || r || "no code");

  const fetchFkPickupOtc = async (account) => {
    const label = String(account.account_name || account.name);
    setFkOtc({ open: true, account: label, mode: "pickup", loading: true,
               codes: "", note: "" });
    try {
      const { data } = await api.post("/api/flipkart-otc/pickup", { account: label, date: selectedDate });
      if (data.cached) {
        const pickupQty = getQty(`${label}-pickup_qty`);
        const cancelQty = getQty(`${label}-cancel_qty`);
        const totalQty = (scanSummary.find((x) => x.account_name === label) || {}).total_orders ?? 0;
        const sumQty = pickupQty + cancelQty;
        setFkOtc({
          open: true,
          account: label,
          mode: "pickup",
          loading: false,
          codes: data.code || "",
          note: data.code ? "" : "No saved Pickup OTC found",
          sub: `P ${pickupQty} + C ${cancelQty}` +
               (totalQty ? ` = ${sumQty} / ${totalQty}` : "") +
               " · saved",
        });
        return;
      }
      const id = data.id;
      const started = Date.now();
      const poll = setInterval(async () => {
        try {
          const { data: st } = await api.get(`/api/flipkart-otc/pickup/${id}`);
          const row = st.pickupOtc && (st.pickupOtc.results || [])[0];
          if (!st.done && !row) {
            if (Date.now() - started > 150000) {
              clearInterval(poll);
              setFkOtc({ open: true, account: label, mode: "pickup",
                         loading: false, codes: "", note: "timed out — try again" });
            }
            return;
          }
          clearInterval(poll);
          if (!row) {
            setFkOtc({ open: true, account: label, mode: "pickup", loading: false,
                       codes: "", note: "no response from the runner" });
            return;
          }

          // The Pickup quantity stays a HUMAN count on purpose. Flipkart's
          // pendingToDispatch is a live snapshot of what is ready right now, not
          // how many of today's packets are being handed over, so filling from it
          // wrote the wrong number (11 vs 21 uploaded). More importantly, auto-
          // filling would make the match check below always pass by construction
          // and stop catching miscounts — which is the whole reason it exists.
          // Flipkart's figure is shown alongside as a cross-check, nothing more.
          const pickupQty = getQty(`${label}-pickup_qty`);

          // 2) the old guard: Pickup + Cancel must equal the scanned total
          const cancelQty = getQty(`${label}-cancel_qty`);
          const summary = scanSummary.find((x) => x.account_name === label);
          const totalQty = summary?.total_orders ?? 0;
          const sumQty = pickupQty + cancelQty;
          if (totalQty > 0 && sumQty !== totalQty) {
            setFkOtc({
              open: true, account: label, mode: "pickup", loading: false, codes: "",
              note: `Fill counts`,
            sub: `P ${pickupQty} + C ${cancelQty} = ${sumQty} / ${totalQty}` +
                 (row.to_dispatch !== null && row.to_dispatch !== undefined
                   ? ` · FK ${row.to_dispatch}` : ""),
            });
            return;
          }

          setFkOtc({
            open: true, account: label, mode: "pickup", loading: false,
            codes: row.otc || "",
            note: row.otc ? "" : otcReason(row.reason),
            sub: `P ${pickupQty} + C ${cancelQty}` +
                 (totalQty ? ` = ${sumQty} / ${totalQty}` : ""),
          });
        } catch {
          /* keep polling through a blip */
        }
      }, 2000);
    } catch (err) {
      setFkOtc({ open: true, account: label, mode: "pickup", loading: false,
                 codes: "", note: err?.response?.data?.error || "could not start the fetch" });
    }
  };

  // Myntra rows can also be fetched live, via the Myntra runner's six always-open
  // browsers on the server. It is much slower than Flipkart (they share one
  // residential SOCKS tunnel, so a fetch can take minutes), hence the long
  // timeout and the explicit "fetching" state.
  const isMyntraOtcAccount = (account) =>
    /^(MYS|MYE)\s+(HAMBERG|SLOWLORIS|OUTFIT|CRYPTIC|CALM|DIVINE)\b/i.test(
      String(account?.account_name || account?.name || ""));

  const fetchMyntraOtc = async (account, kind) => {
    const label = String(account.account_name || account.name);
    const mode = kind === "return" ? "return" : "pickup";
    setFkOtc({ open: true, account: label, mode, loading: true, codes: "", note: "" });
    try {
      const { data } = await api.post(`/api/myntra-otc/${mode}`, { account: label, date: selectedDate });
      if (data.cached) {
        setFkOtc({
          open: true,
          account: label,
          mode,
          loading: false,
          codes: data.code || "",
          note: data.code ? "" : (data.note || "no code"),
          sub: `${data.channel || ""} · ${data.journey || (mode === "return" ? "Return" : "Forward")} · saved`,
        });
        return;
      }
      const id = data.id;
      const channel = data.channel;
      const started = Date.now();
      const poll = setInterval(async () => {
        try {
          const { data: st } = await api.get(
            `/api/myntra-otc/${mode}/${id}?channel=${encodeURIComponent(channel)}`);
          if (st.done) {
            clearInterval(poll);
            setFkOtc({ open: true, account: label, mode, loading: false,
                       codes: st.code || "",
                       note: st.code ? "" : (st.note || "no code"),
                       sub: `${channel} · ${st.journey}` });
          } else if (Date.now() - started > 480000) {
            clearInterval(poll);
            setFkOtc({ open: true, account: label, mode, loading: false, codes: "",
                       note: "timed out (Myntra proxy slow) — try again" });
          }
        } catch { /* keep polling through a blip */ }
      }, 1000);   // the fetch itself is now ~1-3s; a 3s poll was the slowest part
    } catch (err) {
      setFkOtc({ open: true, account: label, mode, loading: false, codes: "",
                 note: err?.response?.data?.error || "could not start the fetch" });
    }
  };

  const handleReturnOtc = async (account) => {
    // Flipkart: ask START or END, then fetch live from the seller portal.
    if (isFlipkartAccount(account)) {
      setFkPick({ open: true, account });
      return;
    }
    if (isMyntraOtcAccount(account)) {
      // Myntra has no separate START/END — one return code, so the count rule
      // applies to it directly.
      const g = otcGuard(account, "return");
      if (!g.ok) { alert(g.why); return; }
      fetchMyntraOtc(account, "return");
      return;
    }
    try {
      console.log("Account object received:", account);

      if (!account || !account.name) {
        console.error("🚨 Error: Account object is not defined or does not have a 'name' property!");
        alert("Error: Account object is missing or Account is undefined.");
        return;
      }
  
      const date = selectedDate;
      console.log(`🔍 Fetching Return OTC for ${account.name} on ${date}`);
  
      const response = await api.get(`/api/get-otc/${account.name}/${date}`);
      const { return_otc, cour_r_qty, cust_r_qty } = response.data;
  
      console.log(`✅ Fetched Return OTC: ${return_otc}, Cour R: ${cour_r_qty}, OBD: ${cust_r_qty} `);
  
      if (parseInt(cust_r_qty) > 0 || parseInt(cour_r_qty) > 0) {
        alert(`✅ Return OTC: ${return_otc}\nCour R: ${cour_r_qty}\nOBD: ${cust_r_qty}`);
      } else {
        alert("❌ FIRST FILL RETURN QTY");
      }
    } catch (error) {
      console.error("❌ Error fetching Return OTC:", error.response?.data?.message || error.message);
      alert("❌ Failed to fetch Return OTC!");
    }
  };  

  const calculateTotalQty = (account) => {
        return printSlotFields.reduce((sum, time) => {
            const key = `${account}-${time}`;
            return sum + (quantities[key] || 0);
        }, 0);
    };
    const calculateColumnTotal = (time) => {
        return accounts.reduce((sum, account) => {
            const key = `${account.name}-${time}`;
            return sum + (quantities[key] || 0);
        }, 0);
    };        

  return   (
      <Box sx={{ padding: "20px" }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: "10px", marginBottom: "20px" }}>
          <h1 style={{ fontSize: "2.5rem", fontWeight: "bold", fontFamily: `'Caveat', 'Patrick Hand', 'Comic Sans MS', cursive`, margin: 0 }}>📦 Prints</h1>
          <Box sx={{ display: "flex", justifyContent: "center", gap: "10px", width: "10%", "@media (min-width: 10px)": { justifyContent: "flex-end", width: "auto" } }}>

              {/* ✅ OTC Button */}
              <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={() => setOtcModalOpen(true)}
                  sx={{ 
                      flexGrow: 1, 
                      minWidth: "20px",
                      "@media (min-width: 50px)": {
                          flexGrow: 0,
                          minWidth: "10px"
                      }
                  }}
              >
                  OTC
              </Button>
              
              {/* ✅ Date Selector */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton
                    size="small"
                    onClick={() => {
                    const formatted = addDaysToDateString(selectedDate, -1);
                    setSelectedDate(formatted);
                    fetchEntriesByDate(formatted);
                    }}
                >
                    <ChevronLeftIcon sx={{ fontSize: 20, color: 'black' }} />
                </IconButton>

                <TextField
                    type="date"
                    value={selectedDate}
                    onChange={handleDateChange}
                    sx={{ width: 140 }}
                    inputProps={{
                    style: { padding: '6px', textAlign: 'center' }
                    }}
                />

                <IconButton
                    size="small"
                    onClick={() => {
                    const formatted = addDaysToDateString(selectedDate, 1);
                    setSelectedDate(formatted);
                    fetchEntriesByDate(formatted);
                    }}
                >
                    <ChevronRightIcon sx={{ fontSize: 20, color: 'black' }} />
                </IconButton>
                </Box>


  
              {/* ✅ Refresh Table Button */}
              <Button
                  variant="contained"
                  color="primary"
                  onClick={() => fetchAccounts()}
                  sx={{ 
                      flexGrow: 1,
                      minWidth: "10px",
                      marginRight: "40px",  // Adjust the gap to the right
                      "@media (min-width: 100px)": {
                          flexGrow: 0,
                          minWidth: "10px"
                      }
                  }}
              >
                  <RefreshIcon />
              </Button>
              
  
          </Box>
      </Box>
  
  
          {/* ✅ Orders Table */}
            <TableContainer component={Paper} style={{ maxHeight: "500px", overflowX: "auto" }}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow style={{ backgroundColor: "#666666" }}>
                            {[
                                "Account", ...printSlotLabels,
                                "Total", "Pickup", "Cancel", "Cour R", "OBD", "OTC", "Remarks"
                            ].map((header, index) => (
                                <TableCell
                key={header}
                onClick={() => {
                const columns = document.querySelectorAll(`.qty-col, .qty-header, .qty-time-total`);
                const isHidden = columns[0]?.style.display === "none";
                setQtyHidden(!isHidden);
                requestAnimationFrame(() => {
                    columns.forEach(col => {
                    col.style.display = isHidden ? "table-cell" : "none";
                    });
                });
                }}
                className={printSlotLabels.includes(header) ? "qty-header" : ""}
                style={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    color: "white",
                    backgroundColor: "#666666",
                    borderRight: "1px solid #ddd",
                    whiteSpace: "nowrap",
                    padding: "4px",
                    textAlign: "center",
                    cursor: (index >= 1 && index <= printSlots.length) || index === 0 ? "pointer" : "default",
                    display:
                    ["Total", "Pickup", "Cancel"].includes(header)
                    ? (showPickupCols ? "table-cell" : "none")
                    : ["Cour R", "OBD"].includes(header)
                    ? (showReturnCols ? "table-cell" : "none")
                    : "table-cell"
                }}
            >
            {header === "Account" ? (
  /* existing Account dropdown – KEEP AS IS */
  <FormControl variant="standard" sx={{ minWidth: 130 }}>
    <Select
      multiple
      value={visibleAccounts}
      disableUnderline
      displayEmpty
      onClick={(e) => e.stopPropagation()}
      renderValue={() => "Account ⬇"}
      sx={{ color: "white", fontWeight: "bold", fontSize: "14px" }}
    >
      <MenuItem
        onClick={(e) => {
          e.stopPropagation();
          setVisibleAccounts(
            visibleAccounts.length === allAccounts.length
              ? []
              : allAccounts.map(a => a.name)
          );
        }}
      >
        <input type="checkbox" checked={visibleAccounts.length === allAccounts.length} readOnly />
        Select All
      </MenuItem>

      {allAccounts.map(acc => (
        <MenuItem
          key={acc.name}
          onClick={(e) => {
            e.stopPropagation();
            setVisibleAccounts(prev =>
              prev.includes(acc.name)
                ? prev.filter(a => a !== acc.name)
                : [...prev, acc.name]
            );
          }}
        >
          <input type="checkbox" checked={visibleAccounts.includes(acc.name)} readOnly />
          {acc.name}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
) : header === "OTC" ? (
  /* ✅ NEW OTC DROPDOWN */
  <FormControl variant="standard" sx={{ minWidth: 90 }}>
    <Select
      value=""
      disableUnderline
      displayEmpty
      onClick={(e) => e.stopPropagation()}
      renderValue={() => "OTC ⬇"}
      sx={{ color: "white", fontWeight: "bold", fontSize: "14px" }}
    >
      <MenuItem
        onClick={(e) => {
          e.stopPropagation();
          setShowPickupCols(v => !v);
        }}
      >
        <input type="checkbox" checked={showPickupCols} readOnly />
        Pickup
      </MenuItem>

      <MenuItem
        onClick={(e) => {
          e.stopPropagation();
          setShowReturnCols(v => !v);
        }}
      >
        <input type="checkbox" checked={showReturnCols} readOnly />
        Return
      </MenuItem>
    </Select>
  </FormControl>
) : (
  header
)}
            </TableCell>
                  ))}
              </TableRow>
          </TableHead>
          <TableBody>
              {allAccounts.length > 0 ? (
                  allAccounts
                    .filter(acc => visibleAccounts.includes(acc.name))
                    .map((account, index) => {
                      // Calculate total quantity for the current account
                      const totalQty = printSlotFields
                          .map((time) => parseInt(quantities[`${account.name}-${time}`]) || 0)
                          .reduce((sum, qty) => sum + qty, 0);
  
                      return (
                          <TableRow
                              key={index}
                              style={{
                                  backgroundColor: index % 2 === 0 ? "#f9f9f9" : "#e0e0e0",
                                  borderBottom: "1px solid #ddd",
                                  height: "30px"
                              }}
                          >
                              {/* Account */}
                              <TableCell
                                  style={{ fontWeight: "bold", fontSize: "14px", textAlign: "center", borderRight: "1px solid #ddd", cursor: "pointer" }}
                              >
                                  {account.name}
                              </TableCell>
  
                              {/* Quantity Input Fields */}
                              {printSlotFields.map((time, idx) => {
                                  const key = `${account.name}-${time}`;
                                  return (
                                      <TableCell
                                          key={idx}
                                          className={`qty-col`}
                                          style={{
                                              borderRight: "1px solid #ddd",
                                              minWidth: "20px",
                                              padding: "2px",
                                              textAlign: "center",
                                          }}>
                                          <TextField
                                              type="text"
                                              value={quantities[key] || ""}
                                              onChange={(e) => handleQuantityChange(account.name, time, e.target.value)}
                                              size="small"
                                              fullWidth
                                              variant="standard"
                                              inputProps={{
                                                  style: { textAlign: "center" },
                                                  maxLength: 4,
                                              }}
                                          />
                                      </TableCell>
                                  );
                              })}
  
                              {/* Total Quantity */}
                                <TableCell
                                className="qty-total"
                                style={{
                                    borderRight: "1px solid #ddd",
                                    textAlign: "center",
                                    display: showPickupCols ? "table-cell" : "none"
                                }}
                                >
                                {totalQty}
                                </TableCell>
                              
  
                              {/* Pickup Quantity */}
                              <TableCell
                                style={{
                                    borderRight: "1px solid #ddd",
                                    textAlign: "center",
                                    display: showPickupCols ? "table-cell" : "none"
                                }}
                                >
                                  <TextField
                                      type="text"
                                      value={quantities[`${account.name}-pickup_qty`] || ""}
                                      onChange={(e) => handleQuantityChange(account.name, "pickup_qty", e.target.value)}
                                      size="small"
                                      fullWidth
                                      variant="standard"
                                      inputProps={{ style: { textAlign: "center" }, maxLength: 4 }}
                                  />
                              </TableCell>
  
                              {/* Cancel Quantity */}
                              <TableCell
                                style={{
                                    borderRight: "1px solid #ddd",
                                    textAlign: "center",
                                    display: showPickupCols ? "table-cell" : "none"
                                }}
                                >
                                <TextField
                                    type="text"
                                    value={quantities[`${account.name}-cancel_qty`] || ""}
                                    onChange={(e) => handleQuantityChange(account.name, "cancel_qty", e.target.value)}
                                    size="small"
                                    fullWidth
                                    variant="standard"
                                    inputProps={{ style: { textAlign: "center" }, maxLength: 4 }}
                                />
                                </TableCell>

  
                              {/* Courier Return Quantity */}
                              <TableCell
                                style={{
                                    borderRight: "1px solid #ddd",
                                    textAlign: "center",
                                    display: showReturnCols ? "table-cell" : "none"
                                }}
                                >
                                <TextField
                                    type="text"
                                    value={quantities[`${account.name}-cour_r_qty`] || ""}
                                    onChange={(e) => handleQuantityChange(account.name, "cour_r_qty", e.target.value)}
                                    size="small"
                                    fullWidth
                                    variant="standard"
                                    inputProps={{ style: { textAlign: "center" }, maxLength: 4 }}
                                />
                                </TableCell>


                              {/* Customer Return Quantity */}
                              <TableCell
                                style={{
                                    borderRight: "1px solid #ddd",
                                    textAlign: "center",
                                    display: showReturnCols ? "table-cell" : "none"
                                }}
                                >
                                <TextField
                                    type="text"
                                    value={quantities[`${account.name}-cust_r_qty`] || ""}
                                    onChange={(e) => handleQuantityChange(account.name, "cust_r_qty", e.target.value)}
                                    size="small"
                                    fullWidth
                                    variant="standard"
                                    inputProps={{ style: { textAlign: "center" }, maxLength: 4 }}
                                />
                                </TableCell>
  
                            {/* OTC Links */}
                            <TableCell style={{ borderRight: "1px solid #ddd", whiteSpace: "nowrap", textAlign: "center" }}>
                                <span
                                    onClick={() => handlePickupOtc(account)}
                                    style={{
                                        fontSize: "12px",
                                        fontWeight: "bold",
                                        color: "blue",
                                        cursor: "pointer",
                                        marginRight: "3px",
                                        textDecoration: otcPresence[`${account.name}-pickup_otc`] ? "underline" : "none",
                                        textDecorationColor: otcPresence[`${account.name}-pickup_otc`] ? "red" : "none"
                                    }}
                                >
                                    Pickup
                                </span>
                                <span
                                    onClick={() => handleReturnOtc(account)}
                                    style={{
                                        fontSize: "12px",
                                        fontWeight: "bold",
                                        color: "blue",
                                        cursor: "pointer",
                                        textDecoration: otcPresence[`${account.name}-return_otc`] ? "underline" : "none",
                                        textDecorationColor: otcPresence[`${account.name}-return_otc`] ? "red" : "none"
                                    }}
                                >
                                    Return
                                </span>
                            </TableCell>
                            
                            
                                {/* Remarks */}
                            <TableCell style={{ minWidth: "20px", textAlign: "center" }}>
                                <TextField
                                    placeholder="Remarks"
                                    value={quantities[`${account.name}-remarks`] || ""}
                                    onChange={(e) => handleQuantityChange(account.name, "remarks", e.target.value)}
                                    size="small"
                                    fullWidth
                                    variant="standard"
                                    multiline
                                    inputProps={{
                                        maxLength: 100,
                                        style: { fontSize: "12px" }  // Set font size to small
                                    }}
                                />
                            </TableCell>
                          </TableRow>
                      );
                  })
              ) : (
                  <TableRow>
                      <TableCell colSpan={printSlots.length + 8} align="center" style={{ fontWeight: "bold", color: "#666666" }}>
                      No accounts found
                      </TableCell>
                  </TableRow>
              )}
              {/* ✅ Total Row at Bottom */}
                <TableRow style={{ backgroundColor: "#d0d0d0", fontWeight: "bold" }}>
                    <TableCell style={{ textAlign: "center", fontWeight: "bold" }}>Total</TableCell>
                        {printSlotFields.map((time, idx) => (
                            <TableCell
                            key={idx}
                            className="qty-time-total qty-total"
                            style={{
                                fontWeight: "bold",
                                textAlign: "center"
                            }}
                            >
                            {allAccounts.reduce(
                                (sum, account) =>
                                sum + (parseInt(quantities[`${account.name}-${time}`]) || 0),
                                0
                            )}
                            </TableCell>
                        ))}
                                <TableCell
                                style={{
                                    textAlign: "center",
                                    fontWeight: "bold",
                                    display: showPickupCols ? "table-cell" : "none"
                                }}
                                >
                                {allAccounts.reduce((sum, account) =>
                                    sum +
                                    printSlotFields
                                    .reduce((acc, time) => {
                                        const val = parseInt(quantities[`${account.name}-${time}`]);
                                        return acc + (isNaN(val) ? 0 : val);
                                    }, 0),
                                    0
                                )}
                                </TableCell>

                  <TableCell
                    style={{
                        textAlign: "center",
                        fontWeight: "bold",
                        display: showPickupCols ? "table-cell" : "none"
                    }}
                    >
                    {allAccounts.reduce((sum, account) => {const val = parseInt(quantities[`${account.name}-pickup_qty`]);return sum + (isNaN(val) ? 0 : val);}, 0)}
                  </TableCell>
                  <TableCell
                    style={{
                        textAlign: "center",
                        fontWeight: "bold",
                        display: showPickupCols ? "table-cell" : "none"
                    }}
                    >
                    {allAccounts.reduce((sum, account) => {const val = parseInt(quantities[`${account.name}-cancel_qty`]);return sum + (isNaN(val) ? 0 : val);}, 0)}
                  </TableCell>
                  <TableCell
                    style={{
                        textAlign: "center",
                        fontWeight: "bold",
                        display: showReturnCols ? "table-cell" : "none"
                    }}
                    >
                    {allAccounts.reduce((sum, account) => {const val = parseInt(quantities[`${account.name}-cour_r_qty`]);return sum + (isNaN(val) ? 0 : val);}, 0)}
                  </TableCell>
                  <TableCell
                    style={{
                        textAlign: "center",
                        fontWeight: "bold",
                        display: showReturnCols ? "table-cell" : "none"
                    }}
                    >
                    {allAccounts.reduce((sum, account) => {const val = parseInt(quantities[`${account.name}-cust_r_qty`]);return sum + (isNaN(val) ? 0 : val);}, 0)}
                  </TableCell>
                  <TableCell colSpan={3}></TableCell>
              </TableRow>
          </TableBody>
  <Modal
      open={otcModalOpen}
      onClose={() => setOtcModalOpen(false)}
      aria-labelledby="otc-modal-title"
      aria-describedby="otc-modal-description"
  >
      <Box
          sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 300,
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 4,
              borderRadius: "8px",
              textAlign: "center"
          }}
      >
          <h3>OTC Entry</h3>
  
          {/* Select Account */}
          <FormControl fullWidth margin="normal">
              <InputLabel id="account-select-label">Select Account</InputLabel>
              <Select
                  labelId="account-select-label"
                  value={selectedAccount}
                  onChange={(e) => setSelectedAccount(e.target.value)}
                  variant="outlined"
              >
                  {allAccounts.map((account, idx) => (
                      <MenuItem key={idx} value={account.name}>{account.name}</MenuItem>
                  ))}
              </Select>
          </FormControl>
  
          {/* Pickup OTC */}
          <TextField fullWidth margin="normal" label="Pickup OTC" type="text" value={pickupOtc} onChange={(e) => setPickupOtc(onlyMoney(e.target.value))} variant="outlined" InputProps={{ inputProps: { inputMode: 'decimal', pattern: '[0-9.]*' }, sx: { '& input::-webkit-outer-spin-button,& input::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 }, '& input[type=number]': { MozAppearance: 'textfield' } } }} />
  
          {/* Return OTC */}
          <TextField fullWidth margin="normal" label="Return OTC" type="text" value={returnOtc} onChange={(e) => setReturnOtc(onlyMoney(e.target.value))} variant="outlined" InputProps={{ inputProps: { inputMode: 'decimal', pattern: '[0-9.]*' }, sx: { '& input::-webkit-outer-spin-button,& input::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 }, '& input[type=number]': { MozAppearance: 'textfield' } } }} />
  
          {/* Add Button */}
          <Button
              variant="contained"
              color="primary"
              onClick={handleOtcAdd}
              fullWidth
              style={{ marginTop: "10px" }}
          >
              Add
          </Button>
      </Box>
  </Modal>
  
      </Table>
  </TableContainer>
  <Box>
  <ScanSummary
    selectedDate={selectedDate}
    setSelectedDate={setSelectedDate}
    readOnlyDate={true}
    accounts={allAccounts} // ✅ instead of accounts
    onOpenNotScanned={(group) => { setNsGroup(group || ""); setNsOpen(true); }}
    />
  {nsOpen && (
    <NotScannedDialog
      open={nsOpen}
      onClose={() => setNsOpen(false)}
      accounts={allAccounts}
      initialGroup={nsGroup}
    />
  )}
    </Box>

    {/* Flipkart Return OTC — pick START or END (the code only exists once the
        delivery agent is at the location, so it is fetched live, not stored). */}
    <Modal open={fkPick.open} onClose={() => setFkPick({ open: false, account: null })}>
      <Box sx={{ position: "absolute", top: "50%", left: "50%",
                 transform: "translate(-50%, -50%)", bgcolor: "#fff",
                 borderRadius: 2, boxShadow: 24, p: 3, width: 380 }}>
        <h2 style={{ margin: "0 0 6px", fontSize: 20 }}>Return OTC</h2>
        <p style={{ margin: "0 0 4px", fontWeight: 700 }}>
          {String(fkPick.account?.account_name || fkPick.account?.name || "")}
        </p>
        <p style={{ margin: "0 0 18px", fontSize: 13, color: "#555" }}>
          <b>START</b> = the code you give the delivery agent when he arrives.<br />
          <b>END</b> = the code after he finishes scanning the returns
          (fill Cour R / OBD first).
        </p>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button fullWidth variant="contained" color="success"
            sx={{ py: 1.6, fontSize: 17, fontWeight: 800 }}
            onClick={() => { const a = fkPick.account;
                             setFkPick({ open: false, account: null });
                             fetchFkReturnOtc(a, "start"); }}>
            START
          </Button>
          <Button fullWidth variant="contained" color="warning"
            sx={{ py: 1.6, fontSize: 17, fontWeight: 800 }}
            onClick={() => {
              const a = fkPick.account;
              // Same rule as the Myntra return OTC: the END code is only handed
              // over once the returns are counted, so Cour R / OBD must be filled.
              const nm = String(a?.account_name || a?.name || "");
              const cour = getQty(`${nm}-cour_r_qty`);
              const obd = getQty(`${nm}-cust_r_qty`);
              if (cour <= 0 && obd <= 0) {
                alert("❌ FIRST FILL RETURN QTY (Cour R or OBD)");
                return;
              }
              setFkPick({ open: false, account: null });
              fetchFkReturnOtc(a, "end");
            }}>
            END
          </Button>
        </Box>
      </Box>
    </Modal>

    {/* The fetched code, shown big enough to read across the packing table. */}
    <Modal open={fkOtc.open}
           onClose={() => setFkOtc({ ...fkOtc, open: false })}>
      <Box sx={{ position: "absolute", top: "50%", left: "50%",
                 transform: "translate(-50%, -50%)", bgcolor: "#0b3d2e",
                 color: "#fff", borderRadius: 2, boxShadow: 24, p: 2.5,
                 width: "min(92vw, 320px)", textAlign: "center",
                 position: "absolute" }}>
        <div style={{ position: "absolute", top: 6, right: 10, fontSize: 11,
                      fontFamily: "monospace", opacity: fkOtc.loading ? 0.9 : 0.55,
                      color: fkOtc.loading ? "#8fd6b4" : "#cfe8db" }}>
          {otcElapsed.toFixed(1)}s
        </div>
        <div style={{ letterSpacing: 2, opacity: 0.85, fontSize: 12 }}>
          {fkOtc.mode === "pickup" ? "PICKUP OTC"
            : fkOtc.mode === "return" ? "RETURN OTC"
            : `RETURN OTC · ${String(fkOtc.mode || "").toUpperCase()}`}
        </div>
        <div style={{ fontWeight: 700, margin: "6px 0 10px" }}>{fkOtc.account}</div>
        {fkOtc.sub ? (
          <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 4,
                        fontFamily: "monospace" }}>{fkOtc.sub}</div>
        ) : null}
        {fkOtc.loading ? (
          <div style={{ fontSize: 20, padding: "18px 0" }}>fetching…</div>
        ) : fkOtc.codes ? (
          <div style={{ fontFamily: "monospace", fontWeight: 800,
                        fontSize: "clamp(40px, 14vw, 60px)",
                        letterSpacing: 6, lineHeight: 1.1, userSelect: "all",
                        cursor: "pointer" }}
               title="Click to copy"
               onClick={() => { try { navigator.clipboard.writeText(fkOtc.codes); } catch { /* */ } }}>
            {fkOtc.codes}
          </div>
        ) : (
          <div style={{ fontSize: 15, fontWeight: 700, color: "#ffd479",
                        padding: "8px 0" }}>
            {fkOtc.note || "no code"}
          </div>
        )}
        <Button size="small" variant="contained" sx={{ mt: 1.2 }}
                onClick={() => setFkOtc({ ...fkOtc, open: false })}>
          Close
        </Button>
      </Box>
    </Modal>

  </Box>
  )}
  
export default PrintsPage;
